import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import {
  json,
  Link,
  redirect,
  useActionData,
  useNavigate,
  useNavigation,
  useRouteLoaderData,
  useSubmit,
} from "react-router-dom";
import { post } from "../../../apiClient";
import createFormErrors from "../../../utils/createFormErrors";
import formatNumber from "../../../utils/formatNumber";
import humanize from "../utils/humanize";

export async function action({ params, request }) {
  try {
    await post("audience", `/v1/segments/${params.id}/deactivate`, {
      signal: request.signal,
    });

    return redirect(`/segments/${params.id}`);
  } catch (error) {
    switch (error.status) {
      case 503:
        return json(
          {
            errors: createFormErrors([
              {
                title:
                  "Couldn't mark the segment for deletion. A deactivation job for the segment is likely already running.",
              },
            ]),
          },
          { status: 503 }
        );
      /* istanbul ignore next */
      default:
        throw error;
    }
  }
}

const describeLinkedAudiences = (count) => {
  switch (count) {
    case null:
      return "We couldn't determine how many audiences are linked to this segment.";
    case 0:
      return "No audiences are linked to this segment.";
    case 1:
      return "1 audience is linked to this segment.";
    default:
      return `${formatNumber(count)} audiences are linked to this segment.`;
  }
};

const LinkedAudienceList = ({ audiences }) => (
  <Box
    sx={{
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      maxHeight: 140,
      mb: 1,
      overflowY: "auto",
    }}
  >
    <List aria-label="Linked audiences" dense disablePadding>
      {audiences.map((audience) => (
        <ListItem key={audience.id} divider disablePadding>
          <ListItemButton
            component={Link}
            rel="noopener noreferrer"
            sx={{ py: 0.75 }}
            target="_blank"
            to={`/audiences/${audience.id}`}
          >
            <ListItemText
              primary={humanize(audience)}
              primaryTypographyProps={{ sx: { overflowWrap: "anywhere" } }}
              sx={{ my: 0 }}
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  </Box>
);

const SegmentDeactivationDialog = () => {
  const form = useActionData();
  const navigate = useNavigate();
  const navigation = useNavigation();
  const segment = useRouteLoaderData("segment");
  const submit = useSubmit();
  const linkedAudienceCount = segment.audiences?.length ?? null;

  return (
    <Dialog onClose={() => navigate("..")} open>
      <DialogTitle>Mark {humanize(segment)} for deletion</DialogTitle>
      <DialogContent>
        {form?.errors?.hasOwnProperty("/") && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {form.errors["/"]}
          </Alert>
        )}
        <Alert
          severity={linkedAudienceCount === 0 ? "info" : "warning"}
          sx={{ mb: 1 }}
        >
          {describeLinkedAudiences(linkedAudienceCount)}
        </Alert>
        {linkedAudienceCount > 0 && (
          <LinkedAudienceList audiences={segment.audiences} />
        )}
        <DialogContentText>
          Are you sure you want to mark the segment for deletion? This will
          inactivate the segment and dissociate it from all targeted
          identifiers.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          autoFocus
          disabled={navigation.state === "submitting"}
          onClick={() => navigate("..")}
          variant="outlined"
        >
          Cancel
        </Button>
        <LoadingButton
          loading={navigation.state === "submitting"}
          onClick={() => submit(null, { method: "post" })}
          type="submit"
          variant="contained"
        >
          Mark for deletion
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SegmentDeactivationDialog;
